import React, { createContext, useContext, useEffect, useState } from 'react';
import ApiService from 'services/apiService';
import {
    GPADashboardFilter, GPADashboardResponse, GPADistributionDto,
    GradeLevelDashboardFilter, GradeLevelDashboardResponse, GradeLevelDistributionDto,
    EnrollmentByAdvisorDashboardFilter, EnrollmentByAdvisorDashboardResponse, EnrollmentByAdvisorDistributionDto,
    StudentServiceDashboardFilter, StudentServiceDashboardResponse, StudentServiceDistributionDto,
    StudentServiceYearTotalDto,
} from 'generated/openApi';
import { StudentServiceMetric } from '../types';
import { useDashboardSchoolYear } from './DashboardSchoolYearContext';

const DASHBOARD_AUTO_REFRESH_INTERVAL_MS = 60_000;

interface DashboardYearFilter { schoolYears: number[]; }
interface DashboardYearTotal { year: number; totalStudents: number; }
interface DashboardGrandTotal { totalStudents: number; }
interface DashboardTermType { id: number; name: string; termCount?: number; }

interface DistributionContextValue<T, F extends DashboardYearFilter, Y extends DashboardYearTotal = DashboardYearTotal> {
    data: T[]; monthlyData: T[]; loading: boolean; error: string | null; filter: F;
    activeSchoolYear: number | null; totalsByYear: Y[]; grandTotal: DashboardGrandTotal | null;
    termTypes: DashboardTermType[];
    updateFilter: (updates: Partial<F>) => void; refetch: () => void;
}

function useDistributionData<T, F extends DashboardYearFilter, R extends { activeSchoolYear: number; data: T[]; monthlyData?: T[]; totalsByYear: any[]; grandTotal: DashboardGrandTotal; termTypes?: DashboardTermType[] }, Y extends DashboardYearTotal>(
    initialFilter: F, request: (filter: F) => Promise<R>, errorMessage: string
): DistributionContextValue<T, F, Y> {
    const { schoolYear } = useDashboardSchoolYear();
    const [data, setData] = useState<T[]>([]);
    const [monthlyData, setMonthlyData] = useState<T[]>([]);
    const [totalsByYear, setTotalsByYear] = useState<Y[]>([]);
    const [grandTotal, setGrandTotal] = useState<DashboardGrandTotal | null>(null);
    const [termTypes, setTermTypes] = useState<DashboardTermType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [filter, setFilter] = useState<F>(initialFilter);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (schoolYear) {
            setInitialized(true);
            setFilter(previous => ({ ...previous, schoolYears: [schoolYear] }));
        }
    }, [schoolYear]);

    const fetchData = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError(null);
        try {
            const responseData = await request(filter);
            if (responseData.activeSchoolYear && !activeSchoolYear) {
                setActiveSchoolYear(responseData.activeSchoolYear);
                if (!initialized && filter.schoolYears.length === 0) {
                    setFilter(previous => ({ ...previous, schoolYears: [responseData.activeSchoolYear] }));
                    setInitialized(true);
                    return;
                }
            }
            setData(responseData.data || []);
            setMonthlyData(responseData.monthlyData || []);
            setTotalsByYear((responseData.totalsByYear || []) as Y[]);
            setGrandTotal(responseData.grandTotal || null);
            setTermTypes(responseData.termTypes || []);
        } catch (requestError) {
            console.error(errorMessage, requestError); setError(errorMessage); setData([]); setMonthlyData([]);
        } finally { setLoading(false); }
    };

    // The request function intentionally follows the context's current state;
    // matching the established dashboard-provider fetch lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (initialized) fetchData(); }, [filter]);

    // Keep an open dashboard current without replacing its cards with loading
    // skeletons. Re-entering the tab refreshes immediately; otherwise poll once
    // per minute while the page is visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const refreshIfVisible = () => {
            if (document.visibilityState === 'visible') fetchData(false);
        };
        const intervalId = window.setInterval(refreshIfVisible, DASHBOARD_AUTO_REFRESH_INTERVAL_MS);
        window.addEventListener('focus', refreshIfVisible);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', refreshIfVisible);
        };
    }, [filter]);

    return {
        data, monthlyData, loading, error, filter, activeSchoolYear, totalsByYear, grandTotal, termTypes,
        updateFilter: updates => setFilter(previous => ({ ...previous, ...updates })),
        refetch: () => fetchData(),
    };
}

const GPAContext = createContext<DistributionContextValue<GPADistributionDto, GPADashboardFilter> | undefined>(undefined);
export const GPADataProvider = ({ children }: { children: React.ReactNode }) => (
    <GPAContext.Provider value={useDistributionData<GPADistributionDto, GPADashboardFilter, GPADashboardResponse, DashboardYearTotal>(
        { schoolYears: [] }, filter => ApiService.gpaDashboard.getGPADistribution(filter), 'Failed to load GPA data')}>
        {children}
    </GPAContext.Provider>
);
export const useGPAData = () => {
    const value = useContext(GPAContext); if (!value) throw new Error('useGPAData must be used within GPADataProvider'); return value;
};

const GradeLevelContext = createContext<DistributionContextValue<GradeLevelDistributionDto, GradeLevelDashboardFilter> | undefined>(undefined);
export const GradeLevelDataProvider = ({ children }: { children: React.ReactNode }) => (
    <GradeLevelContext.Provider value={useDistributionData<GradeLevelDistributionDto, GradeLevelDashboardFilter, GradeLevelDashboardResponse, DashboardYearTotal>(
        { schoolYears: [] }, filter => ApiService.gradeLevelDashboard.getGradeLevelDistribution(filter), 'Failed to load grade-level data')}>
        {children}
    </GradeLevelContext.Provider>
);
export const useGradeLevelData = () => {
    const value = useContext(GradeLevelContext); if (!value) throw new Error('useGradeLevelData must be used within GradeLevelDataProvider'); return value;
};

const EnrollmentByAdvisorContext = createContext<DistributionContextValue<EnrollmentByAdvisorDistributionDto, EnrollmentByAdvisorDashboardFilter> | undefined>(undefined);
export const EnrollmentByAdvisorDataProvider = ({ children }: { children: React.ReactNode }) => (
    <EnrollmentByAdvisorContext.Provider value={useDistributionData<EnrollmentByAdvisorDistributionDto, EnrollmentByAdvisorDashboardFilter, EnrollmentByAdvisorDashboardResponse, DashboardYearTotal>(
        { schoolYears: [] }, filter => ApiService.enrollmentByAdvisorDashboard.getEnrollmentByAdvisorDistribution(filter), 'Failed to load enrollment-by-advisor data')}>
        {children}
    </EnrollmentByAdvisorContext.Provider>
);
export const useEnrollmentByAdvisorData = () => {
    const value = useContext(EnrollmentByAdvisorContext);
    if (!value) throw new Error('useEnrollmentByAdvisorData must be used within EnrollmentByAdvisorDataProvider');
    return value;
};

interface StudentServiceContextValue extends DistributionContextValue<StudentServiceDistributionDto, StudentServiceDashboardFilter, StudentServiceYearTotalDto> {
    metric: StudentServiceMetric; setMetric: (metric: StudentServiceMetric) => void;
}
const StudentServiceContext = createContext<StudentServiceContextValue | undefined>(undefined);
export const StudentServiceDataProvider = ({ children }: { children: React.ReactNode }) => {
    const distribution = useDistributionData<StudentServiceDistributionDto, StudentServiceDashboardFilter, StudentServiceDashboardResponse, StudentServiceYearTotalDto>(
        { schoolYears: [] }, filter => ApiService.studentServiceDashboard.getStudentServiceDistribution(filter), 'Failed to load student-service data');
    const [metric, setMetric] = useState<StudentServiceMetric>('participantRate');
    return <StudentServiceContext.Provider value={{ ...distribution, metric, setMetric }}>{children}</StudentServiceContext.Provider>;
};
export const useStudentServiceData = () => {
    const value = useContext(StudentServiceContext); if (!value) throw new Error('useStudentServiceData must be used within StudentServiceDataProvider'); return value;
};
